import React, { useState, useMemo } from "react";
import { Menu } from "@mantine/core";
import Icon from "./icons";
import css from "./Scheduler.module.css";
import { DOW, MON_SHORT, weekLabel, fmtRange, hrsBetween, entryMeta } from "./scheduleUtils";

const idOf = (o) => (typeof o === "object" ? o?._id || o?.id : o);

// Faithful port of the prototype's roster view (rosterInlineHTML): a printable
// document with a Summary-by-Facility page plus one page per facility, a pager,
// and CSV/PDF download. Built from the loaded shifts.
export default function RosterView({ shifts = [], weekStart, onCsv, onPdf, businessName, logo }) {
  const [page, setPage] = useState(0);
  const brandName = (businessName || "").trim() || "Shift Roster";
  const brandInitial = ((businessName || "Ogiso").trim().charAt(0) || "O").toUpperCase();

  const groups = useMemo(() => {
    // staff rows grouped by facility
    const facMap = {};
    shifts
      .filter((s) => s.status !== "open")
      .forEach((s) => {
        const fac = s.facility?.name || "Unassigned facility";
        const sid = s.staff ? idOf(s.staff) : "open";
        facMap[fac] = facMap[fac] || {};
        if (!facMap[fac][sid]) facMap[fac][sid] = { st: s.staff || { name: "Unassigned" }, shifts: [] };
        facMap[fac][sid].shifts.push(s);
      });
    return Object.keys(facMap)
      .sort()
      .map((fac) => {
        const staff = Object.values(facMap[fac]).map((r) => {
          const total = r.shifts.reduce((a, s) => a + (s.allDay ? 0 : hrsBetween(s.startMin, s.endMin)), 0);
          return { ...r, total, ot: Math.max(0, total - 40), shifts: [...r.shifts].sort((a, b) => new Date(a.date) - new Date(b.date) || a.startMin - b.startMin) };
        });
        let sh = 0;
        let h = 0;
        let ot = 0;
        staff.forEach((r) => {
          sh += r.shifts.length;
          h += r.total;
          ot += r.ot;
        });
        const mode = staff.some((r) => r.shifts.some((s) => s.type === "facility")) ? "facility" : "client";
        return { name: fac, staff, shifts: sh, hours: h, ot, mode };
      });
  }, [shifts]);

  const week = weekLabel(weekStart);
  const today = new Date();
  const genStr = `${MON_SHORT[today.getMonth()]} ${today.getDate()}, ${today.getFullYear()}`;
  const pageCount = groups.length + 1;
  const pg = Math.min(Math.max(page, 0), pageCount - 1);
  const isSummary = pg === 0;
  const fac = isSummary ? null : groups[pg - 1];
  const pageLabel = isSummary ? "Summary" : fac.name;

  const docHead = (subtitle, metaLines = []) => (
    <div className={css.rdHead}>
      <div className={css.rdBrand}>
        <span className={css.rdBrandMark} style={logo ? { padding: 0, overflow: "hidden" } : undefined}>
          {logo ? (
            <img src={logo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit" }} onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement.textContent = brandInitial; }} />
          ) : (
            brandInitial
          )}
        </span>
        <div>
          <div className={css.rbT}>{brandName}</div>
          <div className={css.rbS}>{subtitle}</div>
        </div>
      </div>
      <div className={css.rdMeta}>
        <div className={css.rmPeriod}>{week}</div>
        {metaLines.map((m) => (
          <div key={m}>{m}</div>
        ))}
        <div>Generated {genStr}</div>
      </div>
    </div>
  );

  const staffBlock = (r) => (
    <div className={css.rdStaff} key={idOf(r.st) || r.st.name}>
      <div className={css.rdStaffH}>
        <div>
          <span className={css.rsN}>{r.st.name}</span>
          {r.st.position ? <span className={css.rsR}>{r.st.position}</span> : null}
        </div>
        <div className={css.rsTot}>
          {r.total.toFixed(1)}h{r.ot > 0 ? <> · <span className="ot">{r.ot.toFixed(1)}h OT</span></> : null}
        </div>
      </div>
      {r.shifts.map((s) => {
        const isFacShift = s.type === "facility";
        const cName = isFacShift ? "" : entryMeta(s).clientName || s.client?.name || "Client visit";
        const d = new Date(s.date);
        return (
          <div className={css.rdShiftLine} key={idOf(s)}>
            <div className={css.rlTop}>
              <span className={css.rlDay}>{DOW[d.getDay()]} {MON_SHORT[d.getMonth()]} {d.getDate()}</span>
              <span className={css.rlTime}>{s.allDay ? "All day" : fmtRange(s.startMin, s.endMin)}</span>
              <span className={css.rlName}>{isFacShift ? "Facility shift" : cName}</span>
              <span className={css.rlHrs}>{(s.allDay ? 0 : hrsBetween(s.startMin, s.endMin)).toFixed(1)}h</span>
            </div>
            {!isFacShift && s.service ? (
              <div className={css.rlDetail}>
                <span className={css.rlSvc}><b>Services:</b> {s.service}</span>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );

  const totals = groups.reduce(
    (a, g) => ({ shifts: a.shifts + g.shifts, hours: a.hours + g.hours, staff: a.staff + g.staff.length, ot: a.ot + g.ot }),
    { shifts: 0, hours: 0, staff: 0, ot: 0 }
  );

  return (
    <div className={css.rosterView}>
      <div className={css.rosterBar}>
        <span className={css.rvTitle}><Icon name="calendar" size={16} /> Shift Roster · {week}</span>
        <div className={css.rdPager}>
          <button type="button" className={css.rdNav} onClick={() => setPage(pg - 1)} disabled={pg === 0} aria-label="Previous page"><Icon name="chevronLeft" size={15} /></button>
          <span className={css.rdPglabel}>{pageLabel} · {pg + 1} / {pageCount}</span>
          <button type="button" className={css.rdNav} onClick={() => setPage(pg + 1)} disabled={pg === pageCount - 1} aria-label="Next page"><Icon name="chevron" size={15} /></button>
        </div>
        <Menu shadow="md" width={180} position="bottom-end">
          <Menu.Target>
            <button type="button" className={css.tbBtn}>
              <Icon name="download" size={15} /> Download full roster <Icon name="chevronDown" size={13} />
            </button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item onClick={onPdf}>Export as PDF</Menu.Item>
            <Menu.Item onClick={onCsv}>Export as CSV</Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </div>

      <div className={css.rosterScroll}>
        <div className={css.rosterDoc}>
          {isSummary ? (
            <section className={css.rdPage}>
              {docHead("Staff Shift Roster", [`All staff · ${totals.staff} caregiver${totals.staff === 1 ? "" : "s"}`])}
              <div className={css.rdSectionT}>Summary by Facility</div>
              <table className={css.rdTbl}>
                <thead>
                  <tr><th>Facility</th><th className="num">Staff</th><th className="num">Shifts</th><th className="num">Hours</th><th className="num">OT</th></tr>
                </thead>
                <tbody>
                  {groups.length === 0 ? (
                    <tr><td colSpan={5} style={{ color: "var(--app-text-muted)" }}>No shifts in this period yet.</td></tr>
                  ) : (
                    groups.map((g) => (
                      <tr key={g.name}>
                        <td>{g.name}</td>
                        <td className="num">{g.staff.length}</td>
                        <td className="num">{g.shifts}</td>
                        <td className="num">{g.hours.toFixed(1)}</td>
                        <td className="num">{g.ot > 0 ? g.ot.toFixed(1) : "-"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                {groups.length > 0 && (
                  <tfoot>
                    <tr><td>Total</td><td className="num">{totals.staff}</td><td className="num">{totals.shifts}</td><td className="num">{totals.hours.toFixed(1)}</td><td className="num">{totals.ot.toFixed(1)}</td></tr>
                  </tfoot>
                )}
              </table>
              <div className={css.rdFoot}><span>Ogiso Scheduler</span><span>Week of {week}</span></div>
            </section>
          ) : (
            <section className={css.rdPage}>
              {docHead("Staff Shift Roster")}
              <div className={css.rdFacTitle}>
                <div className={css.rftName}>{fac.name}</div>
                <div className={css.rftMeta}>
                  {fac.mode === "facility" ? "Facility shifts" : "Client visits"} · {fac.staff.length} staff · {fac.shifts} shifts · {fac.hours.toFixed(1)}h · Week of {week}
                </div>
              </div>
              {fac.staff.length ? fac.staff.map(staffBlock) : <div className={css.rdOff}>No shifts this week.</div>}
              <div className={css.rdFoot}><span>Ogiso Scheduler · {fac.name}</span><span>Week of {week}</span></div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
